import React, { useCallback } from 'react';
import {
  FinancialServicesUIProvider,
  RedirectHandler,
  ResultCodeEnum,
  TransactionCategory,
} from '@shipstation/financial-services-ui-components';
import { useDispatch } from 'react-redux';
import { useRouteMatch, useHistory } from 'react-router-dom';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { useGlobalModalLaunchContext } from '../contexts/modal-context/global-modal-context';
import { updatePaymentMethod } from '../financial-services-ui/api';
import {
  ADD_PAYMENT_METHOD,
  SUBSCRIPTION_CHANGE_PLAN,
  WALLET_REGISTRATION,
  WALLET_UPDATE_PAYMENT,
  SUBSCRIPTION_BILLING,
  useFsuiContext,
  FsuiContextProvider,
} from '../financial-services-ui/fsui-context-provider';
import { showGenericSuccessNotification, showGenericErrorNotification } from '../financial-services-ui/notifications';
import { pollForNewPaymentMethod } from '../financial-services-ui/poll-for-new-payment-method';
import { useFulfillmentPlannerContext } from '../fulfillment-planner/context';
import {
  useDeprecatedLocalStorage,
  WALLET_REGISTRATION_DATA,
  WALLET_UPDATE_DATA,
  ADD_PAYMENT_METHOD_DATA,
  SUBSCRIPTION_CHANGE_PLAN_DATA,
} from '../hooks/use-local-storage';
import { useLocale } from '../intl/hooks';
import { usePaymentAndSubscriptionMegaContext } from '../pages/settings/payment-and-subscription/context';
import { changeNickname } from '../pages/settings/providers/providers-list/api';
import { COMPLETE_PAYMENT_REDIRECT } from '../routes/constants';
import { sellerProvidersUpdated } from '../store/seller/seller.actions';
import { useSubmitUpdatePayment } from '../wallet/modals/wallet-payment-update/hooks/use-submit-update-payment';
import { PAYMENT_TAB, TERMS_TAB } from '../wallet/utilities/constants';

// Search parameter constants
const PARAM_CURRENCY_CODE = 'currencyCode';
const PARAM_SESSION_ID = 'sessionId';
const PARAM_SOURCE = 'source';
const PARAM_REDIRECT_RESULT = 'redirectResult';
const PARAM_TRANSACTION_CATEGORY = 'transactionCategory';
const SEARCH_PARAMS = [PARAM_CURRENCY_CODE, PARAM_SESSION_ID, PARAM_SOURCE, PARAM_REDIRECT_RESULT, PARAM_TRANSACTION_CATEGORY];

export const CompletePaymentRedirect = () => {
  // state
  const match = useRouteMatch();
  const history = useHistory();
  const dispatch = useDispatch();
  const { submitForm: submitUpdatePayment } = useSubmitUpdatePayment();
  const queryParams = new URLSearchParams(window.location.search);
  const currencyCode = queryParams.get(PARAM_CURRENCY_CODE);
  const sessionId = queryParams.get(PARAM_SESSION_ID);
  const source = queryParams.get(PARAM_SOURCE);
  const [walletRegistrationData, setWalletRegistrationData] = useDeprecatedLocalStorage(WALLET_REGISTRATION_DATA);
  const [walletUpdateData, setWalletUpdateData] = useDeprecatedLocalStorage(WALLET_UPDATE_DATA);
  const [addPaymentData, setAddPaymentMethodData] = useDeprecatedLocalStorage(ADD_PAYMENT_METHOD_DATA);
  const { reloadData } = usePaymentAndSubscriptionMegaContext();
  const [subscriptionChangePlanData, setChangePlanPaymentMethodData] = useDeprecatedLocalStorage(SUBSCRIPTION_CHANGE_PLAN_DATA);
  const { totalCost } = useFulfillmentPlannerContext() || {};

  const { locale } = useLocale();
  const { apiConfig } = useFsuiContext();
  const { openWalletRegistration, openWalletPaymentUpdate, openBillingInfoModal, openManageSubscriptionDrawer } =
    useGlobalModalLaunchContext();

  // callbacks
  const handlePaymentCompleted = useCallback(
    ({ resultCode }) => {
      const success = resultCode === ResultCodeEnum.Authorised;
      const currentUrl = new URL(window.location.href);
      const replaceUrl = match.url.replace(COMPLETE_PAYMENT_REDIRECT, '');

      // remove only the query params referenced in this file so we don't muck with other application needs
      // this needs to be done because of search rewrites like those done in order.details.epics, which override our redirect behavior
      SEARCH_PARAMS.forEach((param) => {
        currentUrl.searchParams.delete(param);
      });

      const redirect = () => history.replace({ pathname: replaceUrl, search: currentUrl.search });

      if (!source) {
        if (success) {
          showGenericSuccessNotification();
        } else {
          showGenericErrorNotification();
        }
        redirect();
        return;
      }

      // if we need to open the wallet modal to a specific step, determine that somehow and perform it here
      if (source === WALLET_REGISTRATION) {
        if (walletRegistrationData && walletRegistrationData[sessionId]) {
          // on success, update wallet registration data for this sessionId with a success bool
          walletRegistrationData[sessionId].success = success;
          setWalletRegistrationData(walletRegistrationData);
          const walletRegistrationProps = {
            walletId: walletRegistrationData[sessionId]?.walletForm?.WalletId,
            initialFormValues: walletRegistrationData[sessionId]?.walletForm,
            // this is important to re-use the session data used to create the payment method, prevents duplicate session creation and losing ID context
            preloadedSession: walletRegistrationData[sessionId]?.preloadedSession,
            totalCost,
            // walletRegistration has other props that are related to completing label purchase after wallet creation
            // I am not passing these on at this time because we are unable to get the callback function from a centralize place.
          };
          setTimeout(() => {
            if (success) {
              showGenericSuccessNotification();
            } else {
              showGenericErrorNotification();
            }
            openWalletRegistration(walletRegistrationProps, success ? TERMS_TAB : PAYMENT_TAB);

            // redirect inside of setTimeout to allow localeStorage to update correctly before redirecting
            redirect();
          }, 0);
        } else {
          showGenericErrorNotification();
          redirect();
        }
        return;
      }

      // BTC TODO modernize this to use the new endpoint instead of the separated changeNickname and submitUpdatePayment calls
      if (source === WALLET_UPDATE_PAYMENT) {
        if (walletUpdateData && walletUpdateData[sessionId]) {
          // on success, update wallet update data for this sessionId with a success bool
          walletUpdateData[sessionId].success = success;
          setWalletUpdateData(walletUpdateData);

          // on success, execute callbacks
          // on failure, open the modal again and pre-fill the form with the values that were just submitted as applicable
          const promises = [];

          // get form data from localstorage
          const walletUpdateForm = walletUpdateData[sessionId]?.walletForm;
          const nicknameChange = walletUpdateData[sessionId]?.nicknameChange;
          const walletId = walletUpdateData[sessionId]?.walletForm?.WalletId;

          // change nickname if necessary
          let hasError = false;
          if (nicknameChange) {
            const changeNicknamePromise = changeNickname(walletId, nicknameChange)
              .then(() => {
                dispatch(sellerProvidersUpdated());
              })
              .catch(() => {
                hasError = true;
              });
            promises.push(changeNicknamePromise);
          }

          // submit form promise
          const submitFormPromise = submitUpdatePayment(walletUpdateForm)
            .then((data) => {
              if (data.errors) {
                hasError = true;
              }
            })
            .catch(() => {
              hasError = true;
            });
          // if any errors with either of those, show error notification and open the modal again
          promises.push(submitFormPromise);

          // provide other error details besides this generic one?
          // theoretically if nickname fails but the other succeeds, the thing just gets named wrong
          // this has zero meaningful impact and is a very unlikely edge case, so I'm not going to bother with it
          Promise.all(promises).finally(() => {
            if (!hasError) {
              showGenericSuccessNotification();
            }
            if (hasError) {
              showGenericErrorNotification();

              // Opting to force the user to re-enter their payment details if there's an error, can change if behavior sucks
              // note that at time of implementation, opening the modal _always_ creates a new session, so we can't just re-use the old one
              openWalletPaymentUpdate({
                walletId: walletUpdateData[sessionId]?.walletForm?.WalletId,
              });

              // redirect inside of promise resolution to allow localeStorage to update correctly before redirecting
              redirect();
            }
          });
        } else {
          showGenericErrorNotification();
          redirect();
        }
      }

      if (source === ADD_PAYMENT_METHOD || source === SUBSCRIPTION_BILLING) {
        if (success) {
          const { updatePayload } = addPaymentData || {};
          if (updatePayload) {
            // banks don't need/use billing info. Safety-strip
            delete updatePayload.infoUpdateRequest;
            updatePaymentMethod(updatePayload)
              .then((result) => {
                if (!result.success) {
                  throw new Error(result.errorMessage);
                }

                reloadData();
                showGenericSuccessNotification();
              })
              .catch(showGenericErrorNotification);
          }
          setAddPaymentMethodData();
        } else {
          showGenericErrorNotification();
          if (source === SUBSCRIPTION_BILLING) {
            openBillingInfoModal();
          }
        }
        redirect();
      }

      if (source === SUBSCRIPTION_CHANGE_PLAN) {
        if (success) {
          const { payload } = subscriptionChangePlanData ?? {};
          const previousCount = payload?.existingPaymentMethodIds?.length ?? 0;

          pollForNewPaymentMethod({ reloadData, previousCount }).then(([, updatedPaymentMethods]) => {
            if (!payload) {
              openManageSubscriptionDrawer();
              redirect();
              return;
            }

            if (updatedPaymentMethods.length > payload.existingPaymentMethodIds?.length) {
              const currentIds = payload.existingPaymentMethodIds;
              const newPaymentMethod = updatedPaymentMethods.find((pm) => !currentIds.includes(pm.id));
              payload.newPaymentMethod = newPaymentMethod;
            }
            openManageSubscriptionDrawer(payload);
            setChangePlanPaymentMethodData();
            // setTimeout here insures that the localstorage clears correctly before redirecting, since redirects can eat localstorage changes
            setTimeout(() => {
              redirect();
            }, 0);
          });
        } else {
          showGenericErrorNotification();
          redirect();
        }
      }
    },
    [
      match.url,
      source,
      history,
      walletRegistrationData,
      sessionId,
      setWalletRegistrationData,
      openWalletRegistration,
      walletUpdateData,
      setWalletUpdateData,
      submitUpdatePayment,
      dispatch,
      openWalletPaymentUpdate,
      addPaymentData,
      setAddPaymentMethodData,
      reloadData,
      openBillingInfoModal,
      setChangePlanPaymentMethodData,
      subscriptionChangePlanData,
      openManageSubscriptionDrawer,
      totalCost,
    ],
  );

  // conditions
  return (
    <FinancialServicesUIProvider apiConfig={apiConfig} locale={locale} useMantine>
      <RedirectHandler currencyCode={currencyCode} locale={locale} onPaymentCompleted={handlePaymentCompleted} />
      <LoadingIndicator />
    </FinancialServicesUIProvider>
  );
};

export const CompletePaymentRedirectWrapper = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const transactionCategory = queryParams.get('transactionCategory') ?? TransactionCategory.AddFunds;
  const currencyCode = queryParams.get('currencyCode');

  return (
    <FsuiContextProvider currencyCode={currencyCode} shouldSkipSessionCreation transactionCategory={transactionCategory}>
      <CompletePaymentRedirect />
    </FsuiContextProvider>
  );
};
